const GlobalAudioContext = {
  activeElement: null,
};

const APILoader = {
  yt: { loading: false, callbacks: [] },
  vimeo: { loading: false, callbacks: [] },
  loadYouTube(cb) {
    if (window.YT && window.YT.Player) return cb();
    this.yt.callbacks.push(cb);
    if (!this.yt.loading) {
      this.yt.loading = true;
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      window.onYouTubeIframeAPIReady = () => {
        this.yt.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
  loadVimeo(cb) {
    if (window.Vimeo && window.Vimeo.Player) return cb();
    this.vimeo.callbacks.push(cb);
    if (!this.vimeo.loading) {
      this.vimeo.loading = true;
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.onload = () => {
        this.vimeo.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
};

class VideoIntersectionInsert extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._isPlaying = false;
    this._loaded = false;
    this._ready = false;
    this._pendingPlay = false;

    this._provider = this.dataset.provider || "html5";
    this._videoId = this.dataset.videoId;
    this._muted = this.dataset.muted === "true";
    this._controls = this.dataset.controls === "true";
    this._player = null;
    this._isModal = this.dataset.isModal === "true";
    this._pausedOnLoad = this.dataset.pausedOnLoad === "true";
    this._loop = this.dataset.loop !== "false";
  }

  connectedCallback() {
    const hasTemplate = this.querySelector("template[data-video-src]");
    if (!this._videoId && !this.dataset.src && !hasTemplate) return;

    // Modal videos load only on explicit playMedia() (user click), not on scroll into view.
    if (this._isModal) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting;

        if (isVisible && !this._loaded) {
          this.loadMedia();
        }

        if (this._pausedOnLoad) {
          if (!isVisible && this._isPlaying) {
            this.pauseMedia();
          }
        } else {
          if (isVisible && this._loaded && !this._isPlaying) {
            if (this._muted || this._isModal) {
              this.playMedia();
            }
          } else if (!isVisible && this._isPlaying) {
            this.handleViewportExit();
          }
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: [0],
      },
    );

    this._observer.observe(this);
  }

  handleViewportExit() {
    this.pauseMedia();
    if (!this._muted) {
      this.muteMedia();
    }
  }

  _clearPlayer() {
    Array.from(this.children).forEach((child) => {
      if (
        child.hasAttribute("data-video-poster") ||
        child.matches("template[data-video-src]")
      ) {
        return;
      }
      child.remove();
    });
    this._player = null;
  }

  _togglePoster(show) {
    const poster = this.querySelector("[data-video-poster]");
    if (!poster) return;
    poster.hidden = !show;
    poster.style.opacity = show ? "1" : "0";
    poster.style.pointerEvents = show ? "auto" : "none";
  }

  _mountHTML5Video() {
    const template = this.querySelector("template[data-video-src]");
    if (template) {
      const fragment = template.content.cloneNode(true);
      this._player = fragment.querySelector("video");
      if (!this._player) return false;
      this._player.classList.add("relative", "z-0", "h-full", "w-full");
      this._player.loop = this._loop;
      if (this._controls) this._player.controls = true;
      this.appendChild(this._player);
      return true;
    }

    if (this.dataset.src) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = this.dataset.src;
      this._player = tempDiv.querySelector("video");
      if (!this._player) return false;
      this._player.classList.add("relative", "z-0", "h-full", "w-full");
      this._player.loop = this._loop;
      this.appendChild(this._player);
      return true;
    }

    if (this._videoId) {
      this._player = document.createElement("video");
      this._player.src = this._videoId;
      this._player.className =
        `${this.dataset.class || ""} relative z-0 h-full w-full`.trim();
      this._player.loop = this._loop;
      this._player.playsInline = true;
      if (this._muted) this._player.muted = true;
      if (this._controls) this._player.controls = true;
      this.appendChild(this._player);
      return true;
    }

    return false;
  }

  loadMedia() {
    this._clearPlayer();
    this._loaded = true;

    if (this._provider === "youtube") {
      const wrap = document.createElement("div");
      wrap.className = "relative z-0 h-full w-full overflow-hidden";
      if (!this._controls) wrap.style.pointerEvents = "none";
      const container = document.createElement("div");
      container.className = "h-full w-full";
      wrap.appendChild(container);
      this.appendChild(wrap);
      APILoader.loadYouTube(() => this.initYouTube(container));
    } else if (this._provider === "vimeo") {
      const wrap = document.createElement("div");
      wrap.className = "relative z-0 h-full w-full overflow-hidden";
      if (!this._controls) wrap.style.pointerEvents = "none";
      const container = document.createElement("div");
      container.className = "h-full w-full";
      wrap.appendChild(container);
      this.appendChild(wrap);
      APILoader.loadVimeo(() => this.initVimeo(container));
    } else {
      this.loadHTML5();
    }
  }

  loadHTML5() {
    if (!this._mountHTML5Video()) return;

    if (!this._controls) {
      this._player.removeAttribute("controls");
      if (this._muted) {
        this._player.style.pointerEvents = "none";
      } else {
        this._player.style.cursor = "pointer";
        this._player.addEventListener("click", () => {
          if (this._isPlaying) {
            this.pauseMedia();
          } else {
            this.playMedia();
          }
        });
      }
    }

    this._player.addEventListener("play", () => {
      this._isPlaying = true;
      this._togglePoster(false);
    });
    this._player.addEventListener("pause", () => {
      this._isPlaying = false;
      if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
    });

    const startPlayback = () => {
      if (this._isModal && !this._muted && this._player) {
        this._player.muted = true;
      }
      this.playMedia();
    };

    if (this._pendingPlay || (this._muted && !this._pausedOnLoad)) {
      if (this._player.readyState >= 1) {
        startPlayback();
      } else {
        this._player.addEventListener("loadeddata", startPlayback, {
          once: true,
        });
        this._player.load();
      }
    }
  }

  initYouTube(container) {
    const modalAutoplay = this._isModal && this._pendingPlay;
    const autoplayFlag =
      modalAutoplay || (this._muted && !this._pausedOnLoad && !this._isModal)
        ? 1
        : 0;
    const muteFlag = this._muted || this._isModal ? 1 : 0;

    // 动态判断：有控制条就给完整原生体验，无控制条则极限精简
    const playerVars = {
      autoplay: autoplayFlag,
      controls: this._controls ? 1 : 0,
      mute: muteFlag,
      loop: 1,
      playlist: this._videoId,
      playsinline: 1,
      rel: 0,
    };

    if (!this._controls) {
      playerVars.modestbranding = 1;
      playerVars.disablekb = 1;
      playerVars.iv_load_policy = 3;
      playerVars.fs = 0;
      playerVars.cc_load_policy = 0;
    }

    this._player = new YT.Player(container, {
      videoId: this._videoId,
      width: "100%", // 强制满宽满高，修复 UI 控制栏被切除的 Bug
      height: "100%",
      playerVars: playerVars,
      events: {
        onReady: () => {
          this._ready = true;
          if (!this._controls) {
            const iframe = this.querySelector("iframe");
            if (iframe) iframe.style.pointerEvents = "none";
          }
          if (this._pendingPlay) {
            this._pendingPlay = false;
            this.playMedia();
          } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
            this.playMedia();
          }
        },
        onStateChange: (e) => {
          if (e.data === YT.PlayerState.PLAYING) {
            this._isPlaying = true;
            this._togglePoster(false);
          } else if (e.data === YT.PlayerState.PAUSED) {
            this._isPlaying = false;
            if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
          }
        },
      },
    });
  }

  initVimeo(container) {
    const modalAutoplay = this._isModal && this._pendingPlay;
    const shouldAutoplay =
      modalAutoplay || (this._muted && !this._pausedOnLoad && !this._isModal);
    const startMuted = this._muted || this._isModal;

    this._player = new Vimeo.Player(container, {
      id: this._videoId,
      width: "100%",
      height: "100%",
      autoplay: shouldAutoplay,
      controls: this._controls,
      muted: startMuted,
      loop: true,
      background:
        !this._controls && startMuted && !this._pausedOnLoad && !modalAutoplay,
      // 动态判断：只有 controls: true 时才显示原生标题和头像信息
      title: this._controls,
      byline: this._controls,
      portrait: this._controls,
      dnt: true,
    });

    this._player.ready().then(() => {
      this._ready = true;
      this._player.getDuration?.().then((duration) => {
        this._cachedDuration = duration || 0;
      });
      if (this._pendingPlay) {
        this._pendingPlay = false;
        this.playMedia();
      } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
        this.playMedia();
      }
    });

    this._player.on("timeupdate", (data) => {
      this._cachedTime = data?.seconds || 0;
      if (data?.duration) this._cachedDuration = data.duration;
    });

    this._player.on("play", () => {
      this._isPlaying = true;
      this._togglePoster(false);
    });
    this._player.on("pause", () => {
      this._isPlaying = false;
      if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
    });
  }

  getCurrentTime() {
    if (this._provider === "html5" && this._player) {
      return this._player.currentTime || 0;
    }
    if (this._provider === "youtube" && this._player?.getCurrentTime) {
      try {
        return this._player.getCurrentTime() || 0;
      } catch (error) {
        return 0;
      }
    }
    if (this._provider === "vimeo") return this._cachedTime || 0;
    return 0;
  }

  getDuration() {
    if (this._provider === "html5" && this._player) {
      return this._player.duration || 0;
    }
    if (this._provider === "youtube" && this._player?.getDuration) {
      try {
        return this._player.getDuration() || 0;
      } catch (error) {
        return 0;
      }
    }
    if (this._provider === "vimeo") return this._cachedDuration || 0;
    return 0;
  }

  seekTo(seconds) {
    const time = Math.max(0, Number(seconds) || 0);
    if (this._provider === "html5" && this._player) {
      this._player.currentTime = time;
    } else if (this._provider === "youtube" && this._player?.seekTo) {
      this._player.seekTo(time, true);
    } else if (this._provider === "vimeo" && this._player?.setCurrentTime) {
      this._cachedTime = time;
      this._player.setCurrentTime(time);
    }
  }

  playMedia() {
    if (!this._loaded) {
      this._pendingPlay = true;
      this.loadMedia();
      return;
    }

    if (
      (this._provider === "youtube" || this._provider === "vimeo") &&
      !this._ready
    ) {
      this._pendingPlay = true;
      return;
    }

    if (!this._muted) {
      if (
        GlobalAudioContext.activeElement &&
        GlobalAudioContext.activeElement !== this
      ) {
        GlobalAudioContext.activeElement.muteMedia();
      }
      GlobalAudioContext.activeElement = this;
    }

    if (this._provider === "html5" && this._player) {
      if (this._isModal && !this._muted) {
        this._player.muted = true;
      }

      const playPromise = this._player.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this._isPlaying = true;
            this._togglePoster(false);
            if (!this._muted) this._player.muted = false;
          })
          .catch(() => {
            this._isPlaying = false;
          });
      }
    } else if (this._provider === "youtube" && this._player?.playVideo) {
      if (this._isModal && !this._muted) {
        this._player.mute();
      }
      this._player.playVideo();
      if (!this._muted) {
        this._player.unMute();
      }
      this._isPlaying = true;
    } else if (this._provider === "vimeo" && this._player?.play) {
      const playPromise = this._player.play();
      if (!this._muted) {
        playPromise?.then?.(() => this._player.setVolume(1));
      }
      this._isPlaying = true;
    }
  }

  pauseMedia() {
    this._pendingPlay = false;

    if (this._provider === "html5" && this._player) {
      this._player.pause();
    } else if (this._provider === "youtube" && this._player?.pauseVideo) {
      this._player.pauseVideo();
    } else if (this._provider === "vimeo" && this._player?.pause) {
      this._player.pause();
    }
    this._isPlaying = false;
    if (this._pausedOnLoad && !this._isModal) this._togglePoster(true);
  }

  muteMedia() {
    if (this._provider === "html5" && this._player) {
      this._player.muted = true;
    } else if (this._provider === "youtube" && this._player?.mute) {
      this._player.mute();
    } else if (this._provider === "vimeo" && this._player?.setVolume) {
      this._player.setVolume(0);
    }

    if (GlobalAudioContext.activeElement === this) {
      GlobalAudioContext.activeElement = null;
    }
  }

  resetMedia() {
    if (this._provider === "html5" && this._player) {
      this._player.currentTime = 0;
    } else if (this._provider === "youtube" && this._player?.seekTo) {
      this._player.seekTo(0, true);
    } else if (this._provider === "vimeo" && this._player?.setCurrentTime) {
      this._player.setCurrentTime(0);
    }
  }

  resetPlayer() {
    this.pauseMedia();
    this._clearPlayer();
    this._ready = false;
    this._loaded = false;
    this._pendingPlay = false;
    this._togglePoster(true);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}

if (!customElements.get("video-intersection-insert")) {
  customElements.define("video-intersection-insert", VideoIntersectionInsert);
}

class productMediaSwiper extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }
  initSwiper() {
    const swiperEl = this.querySelector(".swiper");
    new Swiper(swiperEl, {
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      spaceBetween: 8,
      loop: true,
      loopAdditionalSlides: 3,
      on: {
        slideChange(swiperInstance) {
          const realIndex = swiperInstance.realIndex;
          console.log("Slide changed to index:", realIndex);
        },
      },
      breakpoints: {
        768: {
          centeredSlides: true,
          spaceBetween: 10,
        },
        1000: {
          centeredSlides: true,
          spaceBetween: 12,
        },
      },
      pagination: {
        el: this.querySelector(".pagination"),
        clickable: true,
        renderBullet: function (index, className) {
          const bulletClasses = `
              ${className}
              rounded-none
              w-8 h-8 bg-[#CCCCCC] transition-all duration-300 block cursor-pointer
              hover:bg-white
              [&.swiper-pagination-bullet-active]:w-50 [&.swiper-pagination-bullet-active]:h-8 [&.swiper-pagination-bullet-active]:bg-[#FEDB1E]
            `;
          return `<span class="${bulletClasses.trim().replace(/\s+/g, " ")}"></span>`;
        },
      },
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
    });
  }
}
if (!customElements.get("product-media-swiper")) {
  customElements.define("product-media-swiper", productMediaSwiper);
}

class SaProductCard extends HTMLElement {
  connectedCallback() {
    this.bindInteractions();
  }

  get isInProductsSwiper() {
    return Boolean(this.closest(".sa-products-swiper"));
  }

  getProductSlide() {
    return this.closest(".sa-products-swiper .swiper-slide");
  }

  getParentSwiper() {
    const swiperEl = this.closest(".sa-products-swiper");
    return swiperEl?.swiper ?? null;
  }

  get mediaEl() {
    return this.querySelector(".sa-product-card-media");
  }

  get hasSecondaryImage() {
    return Boolean(this.querySelector(".sa-product-card-image--secondary"));
  }

  elevateSlide(active) {
    if (!this.isInProductsSwiper) return;

    const slide = this.getProductSlide();
    if (slide) slide.style.zIndex = active ? "2" : "";
  }

  showImage(index) {
    const showSecondary = index === 1;
    this.setPreview(showSecondary);
    this.elevateSlide(showSecondary);
  }

  setPreview(active) {
    this.classList.toggle("is-preview", active);
  }

  bindInteractions() {
    if (this._interactionsBound) return;

    this._interactionsBound = true;
    this._hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    this._mediaSwiped = false;
    this._showPreview = () => this.setPreview(true);
    this._hidePreview = () => this.setPreview(false);

    const media = this.mediaEl;

    // Desktop: only the image area switches to the second image on hover
    this._onEnter = () => {
      if (!this._hoverMq.matches || !this.hasSecondaryImage) return;
      this.elevateSlide(true);
      this._showPreview();
    };

    this._onLeave = () => {
      if (!this._hoverMq.matches) return;
      this.elevateSlide(false);
      this._hidePreview();
    };

    media?.addEventListener("mouseenter", this._onEnter);
    media?.addEventListener("mouseleave", this._onLeave);

    this.querySelectorAll("[data-product-card-dot]").forEach((dot) => {
      dot.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.showImage(Number(dot.dataset.productCardDot));
      });
    });

    this.bindMediaTouch();

    this.querySelectorAll("[data-sa-product-card-link]").forEach((link) => {
      let pointerStartX = 0;
      let pointerMoved = false;

      link.addEventListener("pointerdown", (event) => {
        pointerStartX = event.clientX;
        pointerMoved = false;
        this._mediaSwiped = false;
      });

      link.addEventListener("pointermove", (event) => {
        if (Math.abs(event.clientX - pointerStartX) > 8) {
          pointerMoved = true;
        }
      });

      link.addEventListener("click", (event) => {
        if (pointerMoved || this._mediaSwiped) {
          event.preventDefault();
          return;
        }

        const href = link.dataset.saProductCardLink;
        if (href) window.location.href = href;
      });

      link.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        const href = link.dataset.saProductCardLink;
        if (href) window.location.href = href;
      });
    });

    this._syncHover = () => {
      if (!this._hoverMq.matches) this._hidePreview();
    };

    this._hoverMq.addEventListener("change", this._syncHover);
  }

  bindMediaTouch() {
    const media = this.mediaEl;
    if (!media || !this.hasSecondaryImage) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let moved = false;
    let axis = null; // 'x' | 'y' | null
    let parentSwiperLocked = false;

    const unlockParentSwiper = () => {
      if (!parentSwiperLocked) return;
      const swiper = this.getParentSwiper();
      if (swiper) swiper.allowTouchMove = true;
      parentSwiperLocked = false;
    };

    const lockParentSwiper = () => {
      const swiper = this.getParentSwiper();
      if (!swiper || parentSwiperLocked) return;
      swiper.allowTouchMove = false;
      parentSwiperLocked = true;
    };

    this._onMediaPointerDown = (event) => {
      if (this._hoverMq.matches) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      startX = event.clientX;
      startY = event.clientY;
      tracking = true;
      moved = false;
      axis = null;
      this._mediaSwiped = false;

      // Lock parent carousel immediately so image swipe isn't stolen
      lockParentSwiper();

      try {
        media.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    };

    this._onMediaPointerMove = (event) => {
      if (!tracking || this._hoverMq.matches) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!moved && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        moved = true;
      }

      // Decide axis once, then keep parent locked only for horizontal image swipes
      if (!axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axis === "y") {
          // Vertical scroll — give parent/page back control
          unlockParentSwiper();
        } else {
          lockParentSwiper();
          event.preventDefault();
          event.stopPropagation();
        }
      } else if (axis === "x") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    this._onMediaPointerUp = (event) => {
      if (!tracking || this._hoverMq.matches) return;
      tracking = false;

      try {
        if (media.hasPointerCapture?.(event.pointerId)) {
          media.releasePointerCapture(event.pointerId);
        }
      } catch (_) {
        /* ignore */
      }

      unlockParentSwiper();

      // Ignore taps on pagination dots — they have their own handlers
      if (event.target?.closest?.("[data-product-card-dot]")) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const threshold = 40;

      // Horizontal swipe → switch image
      if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy)) {
        this._mediaSwiped = true;
        this.showImage(dx < 0 ? 1 : 0);
        return;
      }

      // Tap → toggle second image (block link navigation for this gesture)
      if (!moved) {
        this._mediaSwiped = true;
        const showingSecondary = this.classList.contains("is-preview");
        this.showImage(showingSecondary ? 0 : 1);
      }
    };

    this._onMediaPointerCancel = (event) => {
      tracking = false;
      unlockParentSwiper();
      try {
        if (
          event?.pointerId != null &&
          media.hasPointerCapture?.(event.pointerId)
        ) {
          media.releasePointerCapture(event.pointerId);
        }
      } catch (_) {
        /* ignore */
      }
    };

    media.addEventListener("pointerdown", this._onMediaPointerDown);
    media.addEventListener("pointermove", this._onMediaPointerMove, {
      passive: false,
    });
    media.addEventListener("pointerup", this._onMediaPointerUp);
    media.addEventListener("pointercancel", this._onMediaPointerCancel);
  }

  disconnectedCallback() {
    if (!this._interactionsBound) return;

    const media = this.mediaEl;
    media?.removeEventListener("mouseenter", this._onEnter);
    media?.removeEventListener("mouseleave", this._onLeave);
    this._hoverMq?.removeEventListener("change", this._syncHover);

    if (media && this._onMediaPointerDown) {
      media.removeEventListener("pointerdown", this._onMediaPointerDown);
      media.removeEventListener("pointermove", this._onMediaPointerMove);
      media.removeEventListener("pointerup", this._onMediaPointerUp);
      media.removeEventListener("pointercancel", this._onMediaPointerCancel);
    }

    this.elevateSlide(false);
    this._interactionsBound = false;
  }
}

if (!customElements.get("sa-product-card")) {
  customElements.define("sa-product-card", SaProductCard);
}

var RecentlyViewedProducts = class extends HTMLElement {
  async connectedCallback() {
    if (this.searchQueryString === "") {
      return;
    }
    const response = await fetch(
      `/search?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`,
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    const recentlyViewedProductsElement = div.querySelector(
      "recently-viewed-products",
    );
    if (recentlyViewedProductsElement.hasChildNodes()) {
      this.innerHTML = recentlyViewedProductsElement.innerHTML;
    } else {
      this.innerHTML = "";
    }
  }
  get searchQueryString() {
    const items = JSON.parse(
      localStorage.getItem("theme:recently-viewed-products") || "[]",
    );
    if (
      this.hasAttribute("exclude-product-id") &&
      items.includes(parseInt(this.getAttribute("exclude-product-id")))
    ) {
      items.splice(
        items.indexOf(parseInt(this.getAttribute("exclude-product-id"))),
        1,
      );
    }
    return items
      .map((item) => "id:" + item)
      .slice(0, this.productsCount)
      .join(" OR ");
  }
  get sectionId() {
    return this.getAttribute("section-id");
  }
  get productsCount() {
    return this.getAttribute("products-count") || 4;
  }
};
window.customElements.define(
  "recently-viewed-products",
  RecentlyViewedProducts,
);

class ArticleToc extends HTMLElement {
  connectedCallback() {
    const contentSel =
      this.getAttribute("content-selector") || ".article-template__content";
    const titleSel = this.getAttribute("title-selector") || "h2";
    const content = document.querySelector(contentSel);
    if (!content) return;

    const headings = Array.from(content.querySelectorAll(titleSel));
    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    // Shuffle and take up to 6
    const shuffled = headings
      .slice()
      .sort(() => Math.random() - 0.5)
      .slice(0, 6);
    // Re-sort by DOM order so numbering stays logical
    shuffled.sort((a, b) => (a.compareDocumentPosition(b) & 4 ? -1 : 1));

    // Ensure each heading has an id for anchor linking
    shuffled.forEach((h, i) => {
      if (!h.id) {
        h.id =
          "toc-heading-" +
          i +
          "-" +
          h.textContent
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "")
            .slice(0, 48);
      }
    });

    const links = shuffled
      .map(
        (h, i) =>
          `<a href="#${h.id}" class="text-[14px] md:text-[15px] hover:opacity-60 transition-opacity">${i + 1} - <span class="underline">${h.textContent.trim()}</span></a>`,
      )
      .join("");

    this.innerHTML = links;
  }
}
customElements.define("article-toc", ArticleToc);

// 锚点滚动
function findSaAnchorTarget(targetId) {
  if (!targetId) return null;
  const exact = document.querySelector(
    `[data-anchor-target-id='${CSS.escape(targetId)}']`,
  );
  if (exact) return exact;

  const needle = targetId.toLowerCase();
  return (
    Array.from(document.querySelectorAll("[data-anchor-target-id]")).find(
      (el) =>
        (el.getAttribute("data-anchor-target-id") || "").toLowerCase() ===
        needle,
    ) ||
    document.getElementById(targetId) ||
    null
  );
}

function getSaAnchorScrollOffset() {
  const anchors = document.querySelector(".sa-anchors");
  const headerOffset = getSaHeaderBottomOffset();
  const anchorsOffset = anchors ? anchors.offsetHeight : 0;
  return headerOffset + anchorsOffset;
}

function scrollToSaAnchorTarget(target) {
  if (!target) return false;

  const targetTop =
    target.getBoundingClientRect().top +
    window.scrollY -
    getSaAnchorScrollOffset();
  window.scrollTo({
    top: Math.max(targetTop, 0),
    behavior: "auto",
  });
  return true;
}

function initAnchorScrollLinks() {
  document.querySelectorAll("a[data-anchor-id]").forEach((link) => {
    if (link.dataset.anchorScrollBound === "true") return;

    link.dataset.anchorScrollBound = "true";
    link.addEventListener("click", (e) => {
      const targetId = link.dataset.anchorId;
      if (!targetId) return;

      const target = findSaAnchorTarget(targetId);
      if (!target) return;

      e.preventDefault();
      scrollToSaAnchorTarget(target);
    });
  });
}

function initProductViewAnchorJump() {
  if (window.Shopify?.designMode) return;
  if (document.documentElement.dataset.saViewAnchorJumped === "true") return;
  if (!document.body.classList.contains("template-product")) return;

  const anchor = (new URLSearchParams(window.location.search).get("anchor") || "")
    .trim()
    .replace(/^#/, "");
  if (!anchor) return;

  const target = findSaAnchorTarget(anchor);
  if (!target) return;

  document.documentElement.dataset.saViewAnchorJumped = "true";

  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  const clearAnchorQuery = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("anchor")) return;
    url.searchParams.delete("anchor");
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
    history.replaceState(history.state, "", next);
  };

  const jump = () => scrollToSaAnchorTarget(target);
  jump();
  requestAnimationFrame(jump);
  clearAnchorQuery();

  if (document.readyState !== "complete") {
    window.addEventListener(
      "load",
      () => {
        jump();
        requestAnimationFrame(jump);
      },
      { once: true },
    );
  }
}

document.addEventListener("DOMContentLoaded", () => {
  initAnchorScrollLinks();
  initProductViewAnchorJump();
});
document.addEventListener("shopify:section:load", initAnchorScrollLinks);

function bindSaHeader(root) {
  if (root.dataset.storeHeaderBound === "true") return;
  root.dataset.storeHeaderBound = "true";

  const isTransparent = root.dataset.transparent === "true";
  const megaWrap = root.querySelector("[data-sa-header-mega-wrap]");
  const navItems = Array.from(
    root.querySelectorAll("[data-sa-header-nav-item]"),
  );
  const dropdownItems = Array.from(
    root.querySelectorAll("[data-sa-header-nav-dropdown]"),
  );
  const mobileDrawer = root.querySelector("[data-sa-header-mobile-drawer]");
  const searchDrawer = root.querySelector("[data-sa-header-search]");
  let activeNavItem = null;
  let isHovered = false;

  const bindExclusiveDetails = (container, itemSelector) => {
    if (!container) return;

    const items = Array.from(
      container.querySelectorAll(`:scope > ${itemSelector}`),
    );

    items.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        items.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  };

  if (mobileDrawer) {
    bindExclusiveDetails(
      mobileDrawer.querySelector(".sa-header-mobile-drawer__nav"),
      "details.sa-mobile-nav",
    );
    mobileDrawer.querySelectorAll(".sa-mobile-nav__panel").forEach((panel) => {
      bindExclusiveDetails(panel, "details.sa-mobile-subnav");
    });
  }

  const setSolid = (solid) => {
    // Skip no-op toggles — class churn on every scroll frame causes paint jank
    if (root.classList.contains("is-solid") === solid) {
      if (!isTransparent) return;
      const wantTransparent = isTransparent && !solid;
      if (
        root.classList.contains("sa-header--transparent") === wantTransparent
      ) {
        return;
      }
    }

    root.classList.toggle("is-solid", solid);
    root.classList.toggle("sa-header--transparent", isTransparent && !solid);
    root.classList.toggle("sa-header--solid", !isTransparent || solid);
  };

  const getConfiguredHeaderHeight = () => {
    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    if (!isDesktop) {
      const mobile = parseInt(root.dataset.headerHeightMobile || "", 10);
      return Number.isFinite(mobile) && mobile > 0 ? mobile : 44;
    }
    if (
      window.matchMedia("(min-width: 1200px) and (max-width: 1559px)").matches
    ) {
      return 60;
    }
    const parsed = parseInt(root.dataset.headerHeight || "", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 70;
  };

  const syncHeaderHeight = () => {
    const height = getConfiguredHeaderHeight();
    const mobileHeight = parseInt(root.dataset.headerHeightMobile || "", 10);
    const desktopHeight = parseInt(root.dataset.headerHeight || "", 10);

    document.documentElement.style.setProperty(
      "--header-height",
      `${height}px`,
    );
    document.documentElement.style.setProperty(
      "--sticky-header-height",
      `${getSaHeaderBottomOffset()}px`,
    );

    // Overlay-group (country selector) is outside the header section and needs
    // these on :root — otherwise `top: var(--sa-header-mobile-height)` is invalid
    // and the modal collapses to the bottom of the viewport.
    if (Number.isFinite(mobileHeight) && mobileHeight > 0) {
      document.documentElement.style.setProperty(
        "--sa-header-mobile-height",
        `${mobileHeight}px`,
      );
    }
    if (Number.isFinite(desktopHeight) && desktopHeight > 0) {
      document.documentElement.style.setProperty(
        "--sa-header-desktop-height",
        `${desktopHeight}px`,
      );
    }
  };

  let solidFromScroll = false;
  const updateSolidState = () => {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    // Hysteresis so the transparent/solid edge doesn't flicker
    if (solidFromScroll) {
      solidFromScroll = scrollY > 2;
    } else {
      solidFromScroll = scrollY > 12;
    }

    const megaOpen = megaWrap?.classList.contains("is-open");
    const mobileOpen = mobileDrawer?.classList.contains("is-open");
    const searchOpen = searchDrawer?.classList.contains("is-open");
    const countryOpen = document.querySelector(
      "[data-sa-country-selector].is-open",
    );
    const isMobile = window.matchMedia("(max-width: 1199px)").matches;
    setSolid(
      isMobile ||
        solidFromScroll ||
        isHovered ||
        megaOpen ||
        mobileOpen ||
        searchOpen ||
        countryOpen ||
        !isTransparent,
    );
  };

  const setMegaHidden = (el, hidden) => {
    if (!el) return;
    el.classList.toggle("hidden", hidden);
  };

  const syncMegaFooters = (mega, tabId) => {
    const activePanel = mega.querySelector(
      `[data-sa-header-mega-panel][data-tab-id="${tabId}"]`,
    );
    const productCount = parseInt(activePanel?.dataset.productCount || "0", 10);
    const showFooter = productCount > 2;

    mega.querySelectorAll("[data-sa-header-mega-footer]").forEach((footer) => {
      setMegaHidden(footer, !showFooter || footer.dataset.tabId !== tabId);
    });
  };

  const updateMegaProductsActions = (mega, tab) => {
    if (!mega?.classList.contains("sa-header-mega--products") || !tab) return;

    const handle = tab.dataset.collectionHandle || "";
    const collectionUrl = tab.dataset.collectionUrl || "";

    const comparisonWrap = mega.querySelector(
      "[data-sa-header-comparison-link-wrap]",
    );
    const comparisonAnchor = comparisonWrap?.querySelector("a");
    const comparisonBase = comparisonWrap?.dataset.comparisonBase;

    if (comparisonAnchor && comparisonBase) {
      const comparisonUrl = new URL(comparisonBase, window.location.origin);
      if (handle) {
        comparisonUrl.searchParams.set("collection_handle", handle);
      } else {
        comparisonUrl.searchParams.delete("collection_handle");
      }
      comparisonAnchor.href = `${comparisonUrl.pathname}${comparisonUrl.search}${comparisonUrl.hash}`;
    }

    const shopAllWrap = mega.querySelector(
      "[data-sa-header-shop-all-link-wrap]",
    );
    const shopAllAnchor = shopAllWrap?.querySelector("a");
    const shopAllFallback = shopAllWrap?.dataset.shopAllFallback || "";

    if (shopAllAnchor) {
      shopAllAnchor.href =
        collectionUrl || shopAllFallback || shopAllAnchor.href;
    }
  };

  const closeMega = () => {
    if (!megaWrap) return;
    megaWrap.classList.remove("is-open");
    navItems.forEach((item) => {
      item.classList.remove("is-active");
      const trigger = item.querySelector("[data-sa-header-mega-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
    });
    activeNavItem = null;
    updateSolidState();
  };

  const openMega = (navItem) => {
    if (!megaWrap) return;
    root.__saCloseSearch?.();
    const navTitle = navItem.dataset.navTitle;
    const panels = Array.from(megaWrap.querySelectorAll(".sa-header-mega"));
    const matchedPanel = panels.find(
      (panelEl) => panelEl.dataset.navTitle === navTitle,
    );

    panels.forEach((panelEl) => {
      setMegaHidden(panelEl, panelEl !== matchedPanel);
    });

    navItems.forEach((item) => {
      item.classList.toggle("is-active", item === navItem);
      const trigger = item.querySelector("[data-sa-header-mega-trigger]");
      if (trigger) {
        trigger.setAttribute(
          "aria-expanded",
          item === navItem ? "true" : "false",
        );
      }
    });

    if (!matchedPanel) {
      closeMega();
      return;
    }

    setMegaHidden(matchedPanel, false);
    megaWrap.classList.add("is-open");
    activeNavItem = navItem;
    updateSolidState();
    requestAnimationFrame(() => {
      matchedPanel.querySelectorAll("store-header-swiper").forEach((el) => {
        el.ensureSwiper?.();
      });
      if (matchedPanel.classList.contains("sa-header-mega--products")) {
        const activeTab = matchedPanel.querySelector(
          '[data-sa-header-mega-tab][data-active="true"]',
        );
        if (activeTab) {
          syncMegaFooters(matchedPanel, activeTab.dataset.tabId);
          updateMegaProductsActions(matchedPanel, activeTab);
        }
      }
    });
  };

  const isMovingTo = (event, targetEl) => {
    const related = event.relatedTarget;
    return related instanceof Node && targetEl?.contains(related);
  };

  const isMovingToNavItem = (event) => {
    const related = event.relatedTarget;
    return (
      related instanceof Element &&
      Boolean(related.closest("[data-sa-header-nav-item]"))
    );
  };

  navItems.forEach((item) => {
    item.addEventListener("mouseenter", () => openMega(item));
    item.addEventListener("focusin", () => openMega(item));
    item.addEventListener("mouseleave", (event) => {
      if (!megaWrap?.classList.contains("is-open")) return;
      if (activeNavItem !== item) return;
      if (isMovingTo(event, megaWrap)) return;
      if (isMovingToNavItem(event)) return;
      closeMega();
    });
    const trigger = item.querySelector("[data-sa-header-mega-trigger]");
    trigger?.addEventListener("click", (event) => {
      event.preventDefault();
    });
  });

  dropdownItems.forEach((item) => {
    const trigger = item.querySelector("[data-sa-header-dropdown-trigger]");

    const setExpanded = (expanded) => {
      trigger?.setAttribute("aria-expanded", expanded ? "true" : "false");
    };

    item.addEventListener("mouseenter", () => {
      if (megaWrap?.classList.contains("is-open")) closeMega();
      setExpanded(true);
    });

    item.addEventListener("mouseleave", () => {
      setExpanded(false);
    });

    item.addEventListener("focusin", () => {
      if (megaWrap?.classList.contains("is-open")) closeMega();
      setExpanded(true);
    });

    item.addEventListener("focusout", (event) => {
      if (item.contains(event.relatedTarget)) return;
      setExpanded(false);
    });

    trigger?.addEventListener("click", (event) => {
      event.preventDefault();
    });
  });

  root
    .querySelectorAll(
      ".sa-header__logo, .sa-header__icons, [data-country-selector-open]",
    )
    .forEach((el) => {
      el.addEventListener("mouseenter", () => {
        if (megaWrap?.classList.contains("is-open")) closeMega();
      });
    });

  megaWrap?.addEventListener("mouseleave", (event) => {
    if (isMovingToNavItem(event)) return;
    closeMega();
  });

  root.querySelectorAll("[data-sa-header-mega-tab]").forEach((tab) => {
    const switchProductsTab = () => {
      const tabId = tab.dataset.tabId;
      const mega = tab.closest(".sa-header-mega");
      if (!mega) return;
      mega.querySelectorAll("[data-sa-header-mega-tab]").forEach((btn) => {
        const active = btn === tab;
        btn.dataset.active = active ? "true" : "false";
        btn.setAttribute("aria-selected", active ? "true" : "false");
      });
      mega.querySelectorAll("[data-sa-header-mega-panel]").forEach((panel) => {
        setMegaHidden(panel, panel.dataset.tabId !== tabId);
      });
      syncMegaFooters(mega, tabId);
      requestAnimationFrame(() => {
        const activePanel = mega.querySelector(
          `[data-sa-header-mega-panel][data-tab-id="${tabId}"]`,
        );
        activePanel
          ?.querySelectorAll("store-header-swiper")
          .forEach((swiperEl) => {
            swiperEl.ensureSwiper?.();
          });
      });
      updateMegaProductsActions(mega, tab);
    };

    tab.addEventListener("mouseenter", switchProductsTab);
    tab.addEventListener("focus", switchProductsTab);
  });

  const openMobile = () => {
    if (!mobileDrawer) return;
    root.__saCloseSearch?.();
    mobileDrawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    root.classList.add("is-mobile-nav-open");
    const toggle = root.querySelector("[data-sa-header-mobile-toggle]");
    toggle?.setAttribute("aria-expanded", "true");
    toggle?.setAttribute(
      "aria-label",
      toggle.dataset.closeLabel || "Close menu",
    );
    syncSaAnnouncementMobileNavVisibility();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mobileDrawer.classList.add("is-open");
        updateSolidState();
        syncSaAnnouncementMobileNavVisibility();
      });
    });
  };

  const closeMobile = ({ keepNavOpen = false } = {}) => {
    if (!mobileDrawer) return false;
    const wasOpen = mobileDrawer.classList.contains("is-open");
    mobileDrawer.classList.remove("is-open");
    mobileDrawer.setAttribute("aria-hidden", "true");
    if (!keepNavOpen) {
      document.body.style.overflow = "";
      root.classList.remove("is-mobile-nav-open");
      const toggle = root.querySelector("[data-sa-header-mobile-toggle]");
      toggle?.setAttribute("aria-expanded", "false");
      toggle?.setAttribute("aria-label", toggle.dataset.menuLabel || "Menu");
    }
    syncSaAnnouncementMobileNavVisibility();
    updateSolidState();
    return wasOpen;
  };

  const mobileToggle = root.querySelector("[data-sa-header-mobile-toggle]");
  if (mobileToggle) {
    mobileToggle.dataset.menuLabel =
      mobileToggle.getAttribute("aria-label") || "Menu";
    mobileToggle.dataset.closeLabel = "Close menu";
    mobileToggle.addEventListener("click", () => {
      const countryRoot = document.querySelector(
        "[data-sa-country-selector].is-open",
      );
      if (countryRoot && window.closeCountrySelectorModal) {
        window.closeCountrySelectorModal(countryRoot);
        return;
      }
      if (mobileDrawer?.classList.contains("is-open")) closeMobile();
      else openMobile();
    });
  }

  window.__saCloseMobileDrawer = closeMobile;
  window.__saOpenMobileDrawer = openMobile;
  window.__saUpdateHeaderState = updateSolidState;

  let scrollFrame = 0;
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      updateSolidState();
      scheduleSaHeaderBottomOffsetSync();
    });
  };
  const onMouseEnter = () => {
    isHovered = true;
    updateSolidState();
  };
  const onMouseLeave = () => {
    isHovered = false;
    updateSolidState();
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  root.addEventListener("mouseenter", onMouseEnter);
  root.addEventListener("mouseleave", onMouseLeave);
  window.addEventListener("resize", () => {
    syncHeaderHeight();
    updateSolidState();
  });

  syncHeaderHeight();
  syncSaHeaderBottomOffset();
  updateSolidState();
  bindSaHeaderCartDrawer(root);
  bindSaHeaderSearch(root, {
    closeMega,
    closeMobile,
    updateSolidState,
  });
}

function bindSaHeaderSearch(
  root,
  { closeMega, closeMobile, updateSolidState },
) {
  if (root.dataset.saHeaderSearchBound === "true") return;
  root.dataset.saHeaderSearchBound = "true";

  const getSearchDrawer = () => root.querySelector("[data-sa-header-search]");

  const getTriggers = () =>
    Array.from(root.querySelectorAll("[data-sa-header-search-trigger]"));

  const setExpanded = (expanded) => {
    getTriggers().forEach((trigger) => {
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  };

  const openSearch = () => {
    const searchDrawer = getSearchDrawer();
    if (!searchDrawer) return;

    if (typeof closeMega === "function") closeMega();
    if (typeof closeMobile === "function") closeMobile();

    searchDrawer.setAttribute("aria-hidden", "false");
    searchDrawer.classList.add("is-open");
    root.classList.add("is-search-open");
    setExpanded(true);
    document.body.style.overflow = "hidden";
    if (typeof syncSaHeaderBottomOffset === "function") {
      syncSaHeaderBottomOffset();
    }

    updateSolidState?.();
    requestAnimationFrame(() => {
      searchDrawer.querySelector("[data-sa-header-search-input]")?.focus();
    });
  };

  const closeSearch = () => {
    const searchDrawer = getSearchDrawer();
    if (!searchDrawer?.classList.contains("is-open")) return false;

    searchDrawer.classList.remove("is-open");
    searchDrawer.setAttribute("aria-hidden", "true");
    root.classList.remove("is-search-open");
    setExpanded(false);

    const input = searchDrawer.querySelector("[data-sa-header-search-input]");
    if (input) input.value = "";

    if (!root.classList.contains("is-mobile-nav-open")) {
      document.body.style.overflow = "";
    }

    updateSolidState?.();
    return true;
  };

  root.__saOpenSearch = openSearch;
  root.__saCloseSearch = closeSearch;
  window.__saCloseHeaderSearch = closeSearch;

  root.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-sa-header-search-trigger]");
    if (trigger && root.contains(trigger)) {
      event.preventDefault();
      const searchDrawer = getSearchDrawer();
      if (searchDrawer?.classList.contains("is-open")) closeSearch();
      else openSearch();
      return;
    }

    const closer = event.target.closest("[data-sa-header-search-close]");
    if (closer && getSearchDrawer()?.contains(closer)) {
      event.preventDefault();
      closeSearch();
    }
  });

  if (!root.dataset.saHeaderSearchEscapeBound) {
    root.dataset.saHeaderSearchEscapeBound = "true";
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeSearch();
    });
  }
}

function bindSaHeaderCartDrawer(root) {
  root.querySelectorAll("[data-sa-cart-drawer-trigger]").forEach((trigger) => {
    if (trigger.dataset.saCartDrawerBound === "true") return;
    trigger.dataset.saCartDrawerBound = "true";

    trigger.addEventListener(
      "click",
      async (event) => {
        if (theme.settings?.cartType !== "drawer") return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const drawer = document.getElementById("CartDrawer");
        if (!drawer || typeof drawer.show !== "function") return;

        if (drawer.open) {
          drawer.hide();
          trigger.setAttribute("aria-expanded", "false");
          return;
        }

        if (window.theme?.cart?.openDrawer) {
          await window.theme.cart.openDrawer(trigger);
        } else {
          await drawer.show(trigger);
        }

        trigger.setAttribute("aria-expanded", drawer.open ? "true" : "false");
      },
      { capture: true },
    );
  });
}

function shouldHideSaAnnouncementForMobileNav() {
  if (!window.matchMedia("(max-width: 1199px)").matches) return false;

  const header = document.querySelector("[data-sa-header]");
  return header?.classList.contains("is-mobile-nav-open") ?? false;
}

function syncSaAnnouncementMobileNavVisibility() {
  const shouldHide = shouldHideSaAnnouncementForMobileNav();

  document.documentElement.toggleAttribute(
    "data-sa-mobile-nav-open",
    shouldHide,
  );

  document.querySelectorAll("[data-sa-announcement]").forEach((bar) => {
    if (shouldHide) {
      bar.dataset.saMobileNavHidden = "true";
      bar.setAttribute("hidden", "");
      return;
    }

    if (bar.dataset.saMobileNavHidden !== "true") return;

    delete bar.dataset.saMobileNavHidden;
    if (!bar.classList.contains("is-dismissed")) {
      bar.removeAttribute("hidden");
    }
  });

  syncSaAnnouncementHeight();
}

function getVisibleSaAnnouncementBar() {
  return document.querySelector(
    "[data-sa-announcement]:not(.is-dismissed):not([hidden])",
  );
}

function getSaAnnouncementHeight() {
  if (document.documentElement.hasAttribute("data-sa-mobile-nav-open")) {
    return 0;
  }

  const bar = getVisibleSaAnnouncementBar();
  if (!bar) return 0;

  // Full laid-out height only — never shrink on scroll (sticky handles motion)
  return Math.round(bar.offsetHeight || bar.clientHeight || 0);
}

function getSaHeaderBottomOffset() {
  const header = document.querySelector("[data-sa-header]");
  if (!header) return 0;

  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
}

function getSaHeaderTopOffset() {
  return getSaHeaderBottomOffset();
}

let saHeaderBottomOffsetFrame = 0;

function syncSaHeaderBottomOffset() {
  const offset = getSaHeaderBottomOffset();
  const next = `${offset}px`;
  if (
    document.documentElement.style.getPropertyValue(
      "--sa-header-bottom-offset",
    ) === next
  ) {
    return;
  }
  document.documentElement.style.setProperty("--sa-header-bottom-offset", next);
}

function scheduleSaHeaderBottomOffsetSync() {
  if (saHeaderBottomOffsetFrame) return;
  saHeaderBottomOffsetFrame = window.requestAnimationFrame(() => {
    saHeaderBottomOffsetFrame = 0;
    syncSaHeaderBottomOffset();
  });
}

function syncSaAnnouncementHeight() {
  const height = getSaAnnouncementHeight();
  const heightPx = `${height}px`;

  document.documentElement.style.setProperty(
    "--sa-announcement-height",
    heightPx,
  );
  document.documentElement.style.setProperty(
    "--announcement-bar-height",
    heightPx,
  );
  // Match everbest: announcement is not sticky; header sticky top stays 0
  document.documentElement.style.setProperty(
    "--enable-sticky-announcement-bar",
    "0",
  );
  syncSaHeaderBottomOffset();
}

const saAnnouncementObservers = new WeakMap();

function bindSaAnnouncementBar(bar) {
  if (!bar || bar.dataset.saAnnouncementBound === "true") return;
  bar.dataset.saAnnouncementBound = "true";

  if (window.ResizeObserver) {
    const observer = new ResizeObserver(() => {
      syncSaAnnouncementHeight();
    });
    observer.observe(bar);
    saAnnouncementObservers.set(bar, observer);
  }

  bar.addEventListener("sa:announcement-resize", syncSaAnnouncementHeight);
}

function initSaAnnouncementHeight() {
  document
    .querySelectorAll("[data-sa-announcement]")
    .forEach(bindSaAnnouncementBar);
  syncSaAnnouncementHeight();
  window.addEventListener("scroll", scheduleSaHeaderBottomOffsetSync, {
    passive: true,
  });
}

function initSaHeaders() {
  document.querySelectorAll("[data-sa-header]").forEach((root) => {
    bindSaHeader(root);
  });
}

function applySaHeaderTransparentTheme(theme) {
  window.__saHeaderTheme = theme;

  const header = document.querySelector("[data-sa-header]");
  if (!header || header.dataset.transparent !== "true") return;

  const defaultTheme = header.dataset.transparentTheme || "black";
  let resolved = theme ?? defaultTheme;
  if (resolved === "default" || resolved === "") resolved = defaultTheme;
  if (resolved === "dark") resolved = "black";

  const isWhite = resolved === "white";
  header.classList.toggle("sa-header--transparent-white", isWhite);
  header.classList.toggle("sa-header--transparent-black", !isWhite);
  document.documentElement.classList.toggle(
    "sa-header-transparent-white",
    isWhite,
  );
  document.documentElement.classList.toggle(
    "sa-header-transparent-black",
    !isWhite,
  );
}

function initSaBannerHeaderColor() {
  if (window.__saHeaderTheme != null) {
    applySaHeaderTransparentTheme(window.__saHeaderTheme);
    return;
  }

  const heroRoot =
    document.querySelector(".sa-preheat-hero") ||
    document.querySelector(".sa-hero");
  const slide =
    heroRoot?.querySelector(".swiper-slide-active[data-sa-header-color]") ||
    heroRoot?.querySelector(".swiper-slide[data-sa-header-color]");
  if (!slide) return;
  applySaHeaderTransparentTheme(slide.dataset.saHeaderColor);
}

window.applySaHeaderTransparentTheme = applySaHeaderTransparentTheme;

class StoreHeaderSwiper extends HTMLElement {
  getControls() {
    const tabId = this.dataset.tabId;
    const layout = this.closest(".sa-header-mega__layout");
    return (
      layout?.querySelector(
        `[data-sa-header-mega-footer][data-tab-id="${tabId}"]`,
      ) || this.querySelector("[data-swiper-controls]")
    );
  }

  getNavElements() {
    const controls = this.getControls();
    if (!controls) return {};

    return {
      controls,
      prev:
        controls.querySelector(".sa-header-mega__prev") ||
        controls.querySelector(".swiper-button-prev"),
      next:
        controls.querySelector(".sa-header-mega__next") ||
        controls.querySelector(".swiper-button-next"),
      scrollbar: controls.querySelector("[data-swiper-scrollbar]"),
    };
  }

  isPanelVisible() {
    const panel = this.closest("[data-sa-header-mega-panel]");
    if (panel) {
      return !panel.classList.contains("hidden");
    }

    const mega = this.closest(".sa-header-mega");
    return Boolean(mega && !mega.classList.contains("hidden"));
  }

  syncControls(swiper) {
    const { controls } = this.getNavElements();
    if (!controls) return;

    const isProducts = Boolean(this.closest(".sa-header-mega--products"));
    if (isProducts && !this.isPanelVisible()) return;

    controls.classList.toggle("is-locked", swiper.isLocked);
  }

  syncMediaRatio(swiper) {
    if (!this.dataset.productLayout) return;

    const slidesPerView = Math.round(
      swiper.slidesPerView || swiper.params.slidesPerView || 1,
    );
    this.classList.toggle("is-slides-per-view-2", slidesPerView === 2);
  }

  bindNavigation() {
    if (!this._swiper) return;

    const { prev, next, scrollbar } = this.getNavElements();

    if (prev && next) {
      if (!this._swiper.params.navigation) {
        this._swiper.params.navigation = { prevEl: prev, nextEl: next };
      } else {
        this._swiper.params.navigation.prevEl = prev;
        this._swiper.params.navigation.nextEl = next;
      }

      if (this._swiper.navigation) {
        this._swiper.navigation.destroy();
        this._swiper.navigation.init();
        this._swiper.navigation.update();
      }
    }

    if (scrollbar) {
      if (!this._swiper.params.scrollbar) {
        this._swiper.params.scrollbar = { el: scrollbar, draggable: true };
      } else {
        this._swiper.params.scrollbar.el = scrollbar;
      }

      if (this._swiper.scrollbar) {
        this._swiper.scrollbar.destroy();
        this._swiper.scrollbar.init();
        this._swiper.scrollbar.updateSize();
      }
    }
  }

  ensureSwiper() {
    if (typeof Swiper === "undefined") return;

    const isProducts = Boolean(this.closest(".sa-header-mega--products"));
    if (isProducts && !this.isPanelVisible()) return;

    const mega = this.closest(".sa-header-mega");
    if (mega?.classList.contains("hidden")) return;

    const swiperEl = this.querySelector(".swiper");
    if (!swiperEl) return;

    const { prev, next } = this.getNavElements();

    if (this._swiper) {
      const hasNav = Boolean(this._swiper.params.navigation?.prevEl);
      if (prev && next && !hasNav) {
        this._swiper.destroy(true, true);
        this._swiper = null;
        delete this.dataset.storeHeaderSwiperBound;
      } else {
        this.bindNavigation();
        this._swiper.update();
        this.syncControls(this._swiper);
        this.syncMediaRatio(this._swiper);
        return;
      }
    }

    const columns = parseInt(this.dataset.columns || "3", 10) || 3;
    const { scrollbar } = this.getNavElements();
    const isProductSwiper = Boolean(this.dataset.productLayout);

    this._swiper = new Swiper(swiperEl, {
      slidesPerView: columns,
      spaceBetween: isProductSwiper ? 16 : 24,
      autoHeight: !isProductSwiper,
      watchOverflow: true,
      roundLengths: true,
      observer: true,
      observeParents: true,
      navigation:
        prev && next
          ? {
              prevEl: prev,
              nextEl: next,
            }
          : undefined,
      scrollbar: scrollbar
        ? {
            el: scrollbar,
            draggable: true,
          }
        : undefined,
      breakpoints: isProductSwiper
        ? {
            1560: {
              slidesPerView: columns,
              spaceBetween: 24,
            },
          }
        : {
            1200: {
              slidesPerView: columns === 2 ? 2 : 2,
              spaceBetween: 24,
            },
            1560: {
              slidesPerView: columns,
              spaceBetween: 24,
            },
          },
      on: {
        init: (swiper) => {
          this.syncControls(swiper);
          this.syncMediaRatio(swiper);
        },
        resize: (swiper) => {
          this.syncControls(swiper);
          this.syncMediaRatio(swiper);
        },
        breakpoint: (swiper) => {
          this.syncControls(swiper);
          this.syncMediaRatio(swiper);
        },
      },
    });

    this.dataset.storeHeaderSwiperBound = "true";
  }

  connectedCallback() {
    requestAnimationFrame(() => this.ensureSwiper());
  }

  disconnectedCallback() {
    this._swiper?.destroy(true, true);
    this._swiper = null;
    delete this.dataset.storeHeaderSwiperBound;
  }

  updateSwiper() {
    this.ensureSwiper();
  }
}

class StoreHeader extends HTMLElement {
  connectedCallback() {
    bindSaHeader(this);
  }
}

window.SaHScrollTabs = {
  MOBILE_MQ: "(max-width: 767px)",

  scrollActiveTab(tabList, tab, smooth = true) {
    if (!tabList || !tab || !window.matchMedia(this.MOBILE_MQ).matches) return;

    const maxScroll = tabList.scrollWidth - tabList.clientWidth;
    if (maxScroll <= 0) return;

    const target = tab.offsetLeft - (tabList.clientWidth - tab.offsetWidth) / 2;

    tabList.scrollTo({
      left: Math.max(0, Math.min(target, maxScroll)),
      behavior: smooth ? "smooth" : "auto",
    });
  },
};

if (!customElements.get("store-header-swiper")) {
  customElements.define("store-header-swiper", StoreHeaderSwiper);
}

if (!customElements.get("store-header")) {
  customElements.define("store-header", StoreHeader);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initSaAnnouncementHeight();
    initSaHeaders();
    initSaBannerHeaderColor();
  });
} else {
  initSaAnnouncementHeight();
  initSaHeaders();
  initSaBannerHeaderColor();
}

window.addEventListener("resize", () => {
  syncSaAnnouncementMobileNavVisibility();
  scheduleSaHeaderBottomOffsetSync();
});

document.addEventListener("shopify:section:load", (event) => {
  event.target.querySelectorAll?.("[data-sa-announcement]")?.forEach((bar) => {
    bindSaAnnouncementBar(bar);
    syncSaAnnouncementHeight();
  });

  event.target.querySelectorAll?.("[data-sa-header]")?.forEach((root) => {
    bindSaHeader(root);
  });

  initSaBannerHeaderColor();
});

document.addEventListener("shopify:section:unload", (event) => {
  event.target.querySelectorAll?.("[data-sa-announcement]")?.forEach((bar) => {
    const observer = saAnnouncementObservers.get(bar);
    if (observer) {
      observer.disconnect();
      saAnnouncementObservers.delete(bar);
    }
    delete bar.dataset.saAnnouncementBound;
  });
  syncSaAnnouncementHeight();
});
